import { AccountState } from '@/services/auth/types';
import { formatCentsToUsd } from '@/utils/currency';
import { BUILDER_CREDITS_CONFIG, remainingPurchaseHeadroom } from './credits-purchase-config';

/** Shown when a non-Ordway account has no Stripe customer and so can't purchase AI credits. */
export const CREDITS_UNAVAILABLE_MESSAGE =
  'Credit purchases are not available for your account. Contact support to acquire credits.';
const TRIAL_REQUIRES_PAID_MESSAGE = 'You must be on a paid plan to purchase credits.';
const PLAN_STATUS_UNAVAILABLE_MESSAGE = 'Credit purchases are not available for your current plan status.';

export type CreditsPurchaseState = {
  accountState: string;
  isPaused: boolean;
  isTrial: boolean;
};

export type CreditsPurchaseDisableOptions = {
  checkTrial: boolean;
  requireStripeCustomer: boolean;
  hasStripeCustomer?: boolean;
};

// Canceled/suspended/pending-cancellation accounts (and paused ones) can never purchase credits.
function isBlockedPlanStatus(accountState: string, isPaused: boolean): boolean {
  if (isPaused) {
    return true;
  }
  return (
    accountState === AccountState.Canceled ||
    accountState === AccountState.Suspended ||
    accountState === AccountState.PendingCancellation
  );
}

/**
 * Whether a credit purchase is disabled for the account's current state: an optional trial check,
 * the blocked-status check, and an optional Stripe-customer requirement (bypassed for Ordway
 * accounts, which bill through Ordway).
 */
export function isCreditsPurchaseDisabled(
  state: CreditsPurchaseState,
  options: CreditsPurchaseDisableOptions,
): boolean {
  if (options.checkTrial && state.isTrial) {
    return true;
  }
  if (isBlockedPlanStatus(state.accountState, state.isPaused)) {
    return true;
  }
  if (options.requireStripeCustomer && options.hasStripeCustomer !== true) {
    return true;
  }
  return false;
}

/**
 * The tooltip message explaining why a credit purchase is disabled, or `''` when it isn't. The
 * Stripe-customer flag is read synchronously from already-initialized billing state, so there is no
 * loading/error branch.
 */
export function creditsPurchaseDisabledMessage(
  state: CreditsPurchaseState,
  options: CreditsPurchaseDisableOptions & { noStripeMessage?: string },
): string {
  if (options.checkTrial && state.isTrial) {
    return TRIAL_REQUIRES_PAID_MESSAGE;
  }
  if (isBlockedPlanStatus(state.accountState, state.isPaused)) {
    return PLAN_STATUS_UNAVAILABLE_MESSAGE;
  }
  if (options.requireStripeCustomer && options.hasStripeCustomer !== true) {
    return options.noStripeMessage ?? '';
  }
  return '';
}

/**
 * The message shown when the Builder balance is at (or within one minimum purchase of) the account
 * cap, so the smallest purchase would exceed it. One string covers both cases: splitting it into "has
 * reached" and "is near" has not been signed off by product, so keep the reviewed wording until it is.
 */
export function builderBalanceLimitMessage(headroom: number): string {
  const { accountBalanceLimitCents, minQuantity } = BUILDER_CREDITS_CONFIG;
  if (accountBalanceLimitCents == null || headroom >= minQuantity) {
    return '';
  }
  return `Your account has reached the maximum balance of ${formatCentsToUsd(accountBalanceLimitCents, 0)}.`;
}

export interface CreditPurchaseGating {
  /** AI-credits ("Buy credits") CTA gating. */
  isPurchaseCreditsDisabled: boolean;
  purchaseCreditsDisabledMessage: string;
  /** Builder-credits ("Add Funds") CTA gating. */
  isPurchaseBuilderCreditsDisabled: boolean;
  purchaseBuilderCreditsDisabledMessage: string;
  /** Inbox-previews ("Buy previews") CTA gating. */
  isPurchaseInboxPreviewsDisabled: boolean;
  purchaseInboxPreviewsDisabledMessage: string;
  /**
   * Whether the backend refuses Inbox Previews for this account outright
   * (trial or blocked plan status) — the plan-card section hides entirely
   * rather than rendering with dead CTAs. Deliberately narrower than the CTA
   * gating: permission blocks and a missing Stripe customer only disable
   * purchasing, they don't make the feature unavailable.
   */
  isInboxPreviewsUnavailable: boolean;
  /** The current Builder-credit balance and how much more can be purchased before the cap. */
  builderCreditsBalanceCents: number;
  builderCreditsHeadroom: number;
}

export type CreditPurchaseGatingInputs = {
  state: CreditsPurchaseState;
  /** Whether billing edits are blocked (rejected verification / read-only / no permission). */
  editDisabled: boolean;
  /** The tooltip for the edit block, when `editDisabled`. */
  editDisabledMessage: string;
  isOrdwayCustomer: boolean;
  /**
   * Whether Ordway *bills* this account. Distinct from `isOrdwayCustomer`, which only says an
   * `ordway_id` exists — the Inbox Previews gate has to match the drawer's rail branch, and that
   * reads the vendor.
   */
  isOrdwayVendor: boolean;
  isStripeCustomer: boolean;
  balanceCents: number;
  /** Whether the Builder balance query is still loading (its headroom isn't trustworthy yet). */
  isBalanceLoading: boolean;
};

/**
 * Computes the disabled state and tooltip copy for both credit-purchase CTAs. The billing-edit block
 * takes priority; then AI credits gate on trial + Stripe-customer (non-Ordway) + plan status, and
 * Builder credits gate on plan status + a still-loading or capped balance. Pure so it can be unit
 * tested; the hook layer supplies the reactive inputs.
 */
export function computeCreditPurchaseGating(inputs: CreditPurchaseGatingInputs): CreditPurchaseGating {
  const { state, editDisabled, editDisabledMessage, isOrdwayCustomer, isOrdwayVendor, isStripeCustomer } = inputs;

  // Knowingly still on `isOrdwayCustomer`, unlike the previews gate below, which reads the vendor. The
  // AI rail picks its purchase form from the same flag (`useCreditPurchase` passes it to
  // `AiCreditsPurchaseModal`), so the gate and the form have to agree; moving only this one would let
  // the CTA open a form the other flag never selected. Both belong on `isOrdwayVendor` for the reasons
  // spelled out below, but CON-8047 scopes the AI rail out; it needs its own change, gate and form
  // together — tracked as CON-8060.
  const aiOptions: CreditsPurchaseDisableOptions = {
    checkTrial: true,
    requireStripeCustomer: !isOrdwayCustomer,
    hasStripeCustomer: isStripeCustomer,
  };
  const isPurchaseCreditsDisabled = editDisabled || isCreditsPurchaseDisabled(state, aiOptions);
  const purchaseCreditsDisabledMessage = editDisabled
    ? editDisabledMessage
    : creditsPurchaseDisabledMessage(state, { ...aiOptions, noStripeMessage: CREDITS_UNAVAILABLE_MESSAGE });

  // The backend refuses Inbox Previews for trials and blocked plan states, so the CTA gates on both.
  // A Stripe customer is required only on the Stripe rail: an invoice-billed account buys on its bill
  // and has no Stripe customer at all, so requiring one would disable the CTA for exactly the accounts
  // the Ordway form exists for. This must stay on the same flag the drawer branches on
  // (`isOrdwayVendor`) — gating on `isOrdwayCustomer` would enable the CTA for a Metronome account with
  // a stale `ordway_id`, which then gets the Stripe form and no Stripe customer to pay with.
  const inboxPreviewsOptions: CreditsPurchaseDisableOptions = {
    checkTrial: true,
    requireStripeCustomer: !isOrdwayVendor,
    hasStripeCustomer: isStripeCustomer,
  };
  const isPurchaseInboxPreviewsDisabled = editDisabled || isCreditsPurchaseDisabled(state, inboxPreviewsOptions);
  const purchaseInboxPreviewsDisabledMessage = editDisabled
    ? editDisabledMessage
    : creditsPurchaseDisabledMessage(state, { ...inboxPreviewsOptions, noStripeMessage: CREDITS_UNAVAILABLE_MESSAGE });

  const builderCreditsHeadroom = remainingPurchaseHeadroom(BUILDER_CREDITS_CONFIG, inputs.balanceCents);
  const builderOptions: CreditsPurchaseDisableOptions = { checkTrial: false, requireStripeCustomer: false };
  const isPurchaseBuilderCreditsDisabled =
    editDisabled ||
    inputs.isBalanceLoading ||
    isCreditsPurchaseDisabled(state, builderOptions) ||
    builderCreditsHeadroom < BUILDER_CREDITS_CONFIG.minQuantity;

  let purchaseBuilderCreditsDisabledMessage = editDisabled
    ? editDisabledMessage
    : creditsPurchaseDisabledMessage(state, builderOptions);
  if (purchaseBuilderCreditsDisabledMessage === '' && !inputs.isBalanceLoading) {
    purchaseBuilderCreditsDisabledMessage = builderBalanceLimitMessage(builderCreditsHeadroom);
  }

  return {
    isPurchaseCreditsDisabled,
    purchaseCreditsDisabledMessage,
    isPurchaseBuilderCreditsDisabled,
    purchaseBuilderCreditsDisabledMessage,
    isPurchaseInboxPreviewsDisabled,
    purchaseInboxPreviewsDisabledMessage,
    isInboxPreviewsUnavailable: isCreditsPurchaseDisabled(state, { checkTrial: true, requireStripeCustomer: false }),
    builderCreditsBalanceCents: inputs.balanceCents,
    builderCreditsHeadroom,
  };
}
